import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, FeatureGroup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import area from '@turf/area';
import pointOnFeature from '@turf/point-on-feature';
import L from 'leaflet';
import proj4 from 'proj4';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import './MapComponent.scss';
import FileUpload from './FileUpload';
import AppShell from './layout/AppShell';
import GeoTIFFLayer from './GeoTIFFLayer';
import { createField, deleteField, getFields, getGeoTIFFAoi, getGeoTIFFMetadata, updateField, uploadGeoTIFF } from '../services/api';
import { useTranslation } from '../i18n';
import { calculateDamageAssessment, validateDamageGeometries } from '../utils/damageCalculation';
import { normalizeCropGeometryEntry, serializeCropGeometryEntry } from '../utils/cropGeometry';
import { createCropTransferCollection, parseCropTransferCollection } from '../utils/cropTransfer';
import { downloadPdfReport } from '../utils/pdfReport';

// Komponent wewnętrzny do dostępu do mapy
const MapContentWrapper = ({ geoTIFFData, onError }) => {
  const mapInstance = useMap();

  useEffect(() => {
    if (!mapInstance) return;
    const timer = setTimeout(() => mapInstance.invalidateSize(), 100);
    return () => clearTimeout(timer);
  }, [mapInstance, geoTIFFData]);

  return geoTIFFData && mapInstance ? <GeoTIFFLayer geoTIFFData={geoTIFFData} map={mapInstance} onError={onError} /> : null;
};

const MapInstanceBridge = ({ onMapReady }) => {
  const mapInstance = useMap();

  useEffect(() => {
    onMapReady(mapInstance);
  }, [mapInstance, onMapReady]);

  return null;
};

const AutoAoiControl = ({ onClick, disabled }) => {
  const mapInstance = useMap();
  const { t } = useTranslation();

  useEffect(() => {
    if (!mapInstance) return undefined;

    const autoAoiControl = L.control({ position: 'topleft' });
    autoAoiControl.onAdd = () => {
      const container = L.DomUtil.create('div', 'leaflet-bar auto-aoi-control');
      const button = L.DomUtil.create('a', 'auto-aoi-button', container);

      button.href = '#';
      button.title = t('map.aoi.buttonTitle');
      button.setAttribute('role', 'button');
      button.setAttribute('aria-label', t('map.aoi.buttonTitle'));
      button.innerHTML = `
        <svg class="auto-aoi-icon" viewBox="0 0 24 24" width="18" height="18" fill="none"
          stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8" />
          <path d="M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8" />
          <path d="M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16" />
          <path d="M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
          <rect x="8.25" y="8.25" width="7.5" height="7.5" rx="1.2" fill="currentColor" stroke="none" />
        </svg>`;

      if (disabled) {
        button.classList.add('is-disabled');
        button.setAttribute('aria-disabled', 'true');
      }

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);
      L.DomEvent.on(button, 'click', (event) => {
        L.DomEvent.stop(event);
        if (!disabled) {
          onClick();
        }
      });

      return container;
    };

    autoAoiControl.addTo(mapInstance);

    // Ensure the AOI button appears before the leaflet-draw toolbar ("draw a polygon").
    const container = autoAoiControl.getContainer();
    const drawToolbar = container?.parentElement?.querySelector('.leaflet-draw');
    if (container && drawToolbar && drawToolbar !== container) {
      container.parentElement.insertBefore(container, drawToolbar);
    }

    return () => {
      autoAoiControl.remove();
    };
  }, [mapInstance, onClick, disabled, t]);

  return null;
};

const ExclusionModeControl = ({ onClick, active, disabled }) => {
  const mapInstance = useMap();
  const { t } = useTranslation();

  useEffect(() => {
    if (!mapInstance) return undefined;

    const exclusionControl = L.control({ position: 'topleft' });
    exclusionControl.onAdd = () => {
      const container = L.DomUtil.create('div', 'leaflet-bar exclusion-mode-control');
      const button = L.DomUtil.create('a', 'exclusion-mode-button', container);

      const label = t('map.exclusion.buttonTitle');
      button.href = '#';
      button.title = label;
      button.setAttribute('role', 'button');
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.innerHTML = `
        <svg class="exclusion-mode-icon" viewBox="0 0 24 24" width="18" height="18" fill="none"
          stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 5 17 3.5 20.5 8 18.5 18 7 20.5 3.5 14Z" />
          <path d="m9.5 9.5 4-1 2.5 3-2 3.5-4.5-.5-1-2.5Z" stroke-dasharray="1.2 1.1" />
          <path d="m10.5 12.5 3 0" />
        </svg>`;

      if (active) {
        button.classList.add('is-active');
      }
      if (disabled) {
        button.classList.add('is-disabled');
        button.setAttribute('aria-disabled', 'true');
      }

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);
      L.DomEvent.on(button, 'click', (event) => {
        L.DomEvent.stop(event);
        if (!disabled) {
          onClick();
        }
      });

      return container;
    };

    exclusionControl.addTo(mapInstance);

    // Keep the exclusion toggle next to the AOI button, before the draw toolbar.
    const container = exclusionControl.getContainer();
    const drawToolbar = container?.parentElement?.querySelector('.leaflet-draw');
    if (container && drawToolbar && drawToolbar !== container) {
      container.parentElement.insertBefore(container, drawToolbar);
    }

    return () => {
      exclusionControl.remove();
    };
  }, [mapInstance, onClick, active, disabled, t]);

  return null;
};

const BASEMAPS = [
  {
    id: 'osm-standard',
    label: 'OpenStreetMap Standard',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
  {
    id: 'opentopomap',
    label: 'OpenTopoMap',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution:
      'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
    maxZoom: 17,
  },
  {
    id: 'esri-world-imagery',
    label: 'Esri World Imagery',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    maxZoom: 19,
  },
  {
    id: 'osm-hot',
    label: 'OpenStreetMap HOT',
    url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, tiles style by <a href="https://www.hotosm.org/">Humanitarian OpenStreetMap Team</a>',
    maxZoom: 19,
  },
  {
    id: 'carto-voyager',
    label: 'Carto Voyager',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    maxZoom: 20,
  },
  {
    id: 'openrailwaymap-standard',
    label: 'OpenRailwayMap Standard',
    url: 'https://{s}.tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png',
    attribution:
      'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, style: <a href="https://www.openrailwaymap.org/">OpenRailwayMap</a>',
    maxZoom: 19,
  },
];

const SELECTED_BASEMAP_ID = 'osm-standard';
const MAX_INTERACTION_ZOOM = 22;
const CROP_LAYER_BASE_STYLE = { color: '#e53935', weight: 2, fillOpacity: 0.2 };
const CROP_LAYER_HOVER_STYLE = { color: '#f04f42', weight: 2.8, fillOpacity: 0.2 };
const CROP_LAYER_PULSE_STYLE = { color: '#ffd166', weight: 3.8, fillOpacity: 0.24 };
const EXCLUSION_LAYER_STYLE = { color: '#2e7d32', weight: 2, fillColor: '#43a047', fillOpacity: 0.35, dashArray: '5 4' };
const AOI_LAYER_STYLE = { color: '#1e88e5', weight: 3, fill: false, fillOpacity: 0 };
const SPOT_FOCUS_BOUNDS_OPTIONS = { padding: [44, 44], maxZoom: MAX_INTERACTION_ZOOM, duration: 0.55 };
const CROP_TYPES = [
  { value: '', icon: '🌱', labelKey: 'field.cropType.unspecified' },
  { value: 'corn', icon: '🌽', labelKey: 'field.cropType.corn' },
  { value: 'sugar_beet', icon: '🍃', labelKey: 'field.cropType.sugarBeet' },
  { value: 'grain', icon: '🌾', labelKey: 'field.cropType.grain' },
  { value: 'rapeseed', icon: '🌼', labelKey: 'field.cropType.rapeseed' },
  { value: 'potatoes', icon: '🥔', labelKey: 'field.cropType.potatoes' },
  { value: 'other', icon: '🪴', labelKey: 'field.cropType.other' },
];

const getSelectedBasemap = () => BASEMAPS.find((basemap) => basemap.id === SELECTED_BASEMAP_ID) || BASEMAPS[0];
const getDesktopBasemapApiRoot = () => {
  const apiBaseUrl = typeof window !== 'undefined' ? window.desktopConfig?.apiBaseUrl : null;
  if (!apiBaseUrl) {
    return null;
  }
  return apiBaseUrl.replace(/\/api\/?$/, '');
};

proj4.defs('EPSG:2176', '+proj=tmerc +lat_0=0 +lon_0=15 +k=0.999923 +x_0=5500000 +y_0=0 +ellps=GRS80 +units=m +no_defs');
proj4.defs('EPSG:2177', '+proj=tmerc +lat_0=0 +lon_0=18 +k=0.999923 +x_0=6500000 +y_0=0 +ellps=GRS80 +units=m +no_defs');
proj4.defs('EPSG:2178', '+proj=tmerc +lat_0=0 +lon_0=21 +k=0.999923 +x_0=7500000 +y_0=0 +ellps=GRS80 +units=m +no_defs');
proj4.defs('EPSG:2179', '+proj=tmerc +lat_0=0 +lon_0=24 +k=0.999923 +x_0=8500000 +y_0=0 +ellps=GRS80 +units=m +no_defs');
proj4.defs('EPSG:2180', '+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +units=m +no_defs');

const computeLeafletBounds = (bbox, geoKeys, width, height, modelTiepoint, modelPixelScale) => {
  let sourceBbox = bbox;
  if (Array.isArray(modelTiepoint) && modelTiepoint.length >= 6 && Array.isArray(modelPixelScale) && modelPixelScale.length >= 2) {
    const isPixelPoint = geoKeys?.GTRasterTypeGeoKey === 2;
    const pixelWidth = isPixelPoint ? Math.max(0, width - 1) : width;
    const pixelHeight = isPixelPoint ? Math.max(0, height - 1) : height;
    const originX = Number(modelTiepoint[3]);
    const originY = Number(modelTiepoint[4]);
    const maxX = originX + Number(modelPixelScale[0]) * pixelWidth;
    const minY = originY - Number(modelPixelScale[1]) * pixelHeight;
    sourceBbox = [originX, minY, maxX, originY];
  }

  const [minX, minY, maxX, maxY] = sourceBbox;
  const projectedEpsg = geoKeys?.ProjectedCSTypeGeoKey ? `EPSG:${geoKeys.ProjectedCSTypeGeoKey}` : null;

  if (projectedEpsg && proj4.defs(projectedEpsg)) {
    const corners = [
      [minX, minY],
      [minX, maxY],
      [maxX, minY],
      [maxX, maxY],
    ];

    const transformed = corners.map((point) => proj4(projectedEpsg, 'EPSG:4326', point));
    const lons = transformed.map((point) => point[0]);
    const lats = transformed.map((point) => point[1]);

    return [
      [Math.min(...lats), Math.min(...lons)],
      [Math.max(...lats), Math.max(...lons)],
    ];
  }

  return [
    [minY, minX],
    [maxY, maxX],
  ];
};

const areBoundsGeographic = (bounds) => {
  const [[southLat, westLng], [northLat, eastLng]] = bounds;
  return (
    Number.isFinite(southLat) &&
    Number.isFinite(westLng) &&
    Number.isFinite(northLat) &&
    Number.isFinite(eastLng) &&
    southLat >= -90 &&
    northLat <= 90 &&
    westLng >= -180 &&
    eastLng <= 180
  );
};

const getGeoTiffCenterLatLng = (geoTIFFData) => {
  if (!geoTIFFData?.bbox || !Array.isArray(geoTIFFData.bbox) || geoTIFFData.bbox.length !== 4) {
    return null;
  }

  const bounds = computeLeafletBounds(
    geoTIFFData.bbox,
    geoTIFFData.geoKeys,
    geoTIFFData.width,
    geoTIFFData.height,
    geoTIFFData.modelTiepoint,
    geoTIFFData.modelPixelScale,
  );
  if (!areBoundsGeographic(bounds)) {
    return null;
  }

  const [[south, west], [north, east]] = bounds;
  return { lat: (south + north) / 2, lng: (west + east) / 2 };
};

const escapeHtml = (value = '') =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const buildCropFlagSvg = (number) => `
  <svg viewBox="0 0 30 38" width="30" height="38" aria-hidden="true">
    <path
      d="M7 2.5H18.8C21.7 2.5 24 4.8 24 7.7V18.2C24 21.1 21.7 23.4 18.8 23.4H12.9L9.8 31.8L7.2 23.4H7C4.1 23.4 1.8 21.1 1.8 18.2V7.7C1.8 4.8 4.1 2.5 7 2.5Z"
      fill="#e63735"
      stroke="#7a2318"
      stroke-width="1.2"
      stroke-linejoin="round"
    />
    <path
      d="M7.2 5.3H18.5C20.1 5.3 21.4 6.6 21.4 8.2V17.4C21.4 19 20.1 20.3 18.5 20.3H11.4L8.7 27.2L6.5 20.3H7.2C5.6 20.3 4.3 19 4.3 17.4V8.2C4.3 6.6 5.6 5.3 7.2 5.3Z"
      fill="#f04f42"
      opacity="0.9"
    />
    <path d="M18.2 5.4L23.1 5.4L18.8 10.2L18.2 5.4Z" fill="#f8d8d2" opacity="0.8"/>
    <text class="crop-flag-number" x="13.2" y="14.5" text-anchor="middle" dominant-baseline="middle" font-size="11" font-weight="800" fill="#ffffff">${escapeHtml(String(number))}</text>
  </svg>
`;

const createCropFlagIcon = (number) => L.divIcon({
  className: 'crop-flag-marker-wrapper',
  html: `<div class="crop-flag-marker">${buildCropFlagSvg(number)}</div>`,
  iconSize: [30, 38],
  iconAnchor: [15, 34],
});

const CropFlag = ({ number }) => (
  <span
    className="crop-flag-list-icon"
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: buildCropFlagSvg(number) }}
  />
);

const MapComponent = ({ canEditFields = true }) => {
  const { t } = useTranslation();
  const [geoTIFFData, setGeoTIFFData] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loadingReports, setLoadingReports] = useState(true);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadProgressPercent, setUploadProgressPercent] = useState(0);
  const [, setUploadedFilename] = useState('');
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [loadingIndicatorPoint, setLoadingIndicatorPoint] = useState(null);
  const [aoiGeometry, setAoiGeometry] = useState(null);
  const [aoiAreaHa, setAoiAreaHa] = useState(null);
  const [cropItems, setCropItems] = useState([]);
  const [cropDamagePercents, setCropDamagePercents] = useState({});
  const [cropProtectedSpecies, setCropProtectedSpecies] = useState({});
  const [exclusionItems, setExclusionItems] = useState([]);
  const [exclusionDrawMode, setExclusionDrawMode] = useState(false);
  const exclusionDrawModeRef = useRef(false);
  const [remainingFieldDamagePercent, setRemainingFieldDamagePercent] = useState(0);
  const featureGroupRef = useRef(null);
  const mapRef = useRef(null);
  const aoiLayerIdRef = useRef(null);
  const cropListItemRefs = useRef(new Map());
  const cropHighlightTimerRef = useRef(null);
  const cropPulseTimersRef = useRef(new Map());
  const nextSpotNumbersRef = useRef(new Map());
  const reportRestorerRef = useRef(null);
  const restoringReportRef = useRef(false);
  const drawingHandlersRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [expandedReportIds, setExpandedReportIds] = useState(() => new Set());
  const [savingReport, setSavingReport] = useState(false);
  const [reportsError, setReportsError] = useState('');
  const handleRasterError = useCallback(() => {
    setReportsError(t('map.geotiff.tileError'));
  }, [t]);
  const [editingReportId, setEditingReportId] = useState(null);
  const [editingReportTitle, setEditingReportTitle] = useState('');
  const [editingReportNotesId, setEditingReportNotesId] = useState(null);
  const [editingReportNotes, setEditingReportNotes] = useState('');
  const [reportActionId, setReportActionId] = useState(null);
  const [pdfExportReportId, setPdfExportReportId] = useState(null);
  const [activeReportId, setActiveReportId] = useState(null);
  // Blokuje rysowanie podczas ladowania listy i przywracania pola (patrz handleShapeCreated).
  const drawingEnabled = canEditFields && !uploading && !loadingReports;
  // react-leaflet-draw przebudowuje kontrolke, gdy zmieni sie identycznosc callbackow lub opcji draw/edit,
  // a jego wewnetrzny propsRef nie jest aktualizowany. Dlatego handlery i opcje musza byc stabilne przez
  // cala sesje, inaczej pasek narzedzi Leaflet Draw moze zostac trwale usuniety z mapy.
  const drawingHandlers = useMemo(() => ({
    onCreated: (event) => drawingHandlersRef.current.onCreated(event),
    onEdited: (event) => drawingHandlersRef.current.onEdited(event),
    onDeleted: (event) => drawingHandlersRef.current.onDeleted(event),
  }), []);
  const drawOptions = useMemo(() => (canEditFields ? {
    polygon: { allowIntersection: false, showArea: true, shapeOptions: { color: '#1e88e5' } },
    rectangle: { shapeOptions: { color: '#1e88e5' } },
    polyline: false,
    circle: false,
    marker: false,
    circlemarker: false,
  } : {
    polygon: false,
    rectangle: false,
    polyline: false,
    circle: false,
    marker: false,
    circlemarker: false,
  }), [canEditFields]);
  const editOptions = useMemo(() => ({
    edit: canEditFields,
    remove: canEditFields,
  }), [canEditFields]);
  const [highlightedCropKey, setHighlightedCropKey] = useState(null);
  const cropTransferInputRef = useRef(null);
  const [developerToolsEnabled, setDeveloperToolsEnabled] = useState(() => (
    typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('developer') === '1'
  ));
  const getNextSpotNumber = (report) => {
    const reportId = String(report?.id || activeReportId || 'draft');
    const storedNext = Number(report?.next_crop_number);
    const savedCrops = Array.isArray(report?.crop_geometries) ? report.crop_geometries : [];
    const savedMax = savedCrops.reduce((maximum, crop, index) => {
      const { spotNumber } = normalizeCropGeometryEntry(crop, null, index + 1);
      return Math.max(maximum, spotNumber || 0);
    }, 0);
    const activeMax = String(activeReportId) === reportId
      ? cropItems.reduce((maximum, crop, index) => Math.max(maximum, crop.spotNumber || index + 1), 0)
      : 0;
    const nextNumber = Math.max(
      nextSpotNumbersRef.current.get(reportId) || 1,
      Number.isInteger(storedNext) && storedNext > 0 ? storedNext : 1,
      savedMax + 1,
      activeMax + 1
    );
    nextSpotNumbersRef.current.set(reportId, nextNumber);
    return nextNumber;
  };
  const assignNextSpotNumber = (report) => {
    const reportId = String(report?.id || activeReportId || 'draft');
    const spotNumber = getNextSpotNumber(report);
    nextSpotNumbersRef.current.set(reportId, spotNumber + 1);
    return spotNumber;
  };
  const selectedBasemap = useMemo(() => getSelectedBasemap(), []);
  const isDesktopApp = typeof window !== 'undefined' && !!window.desktopConfig?.isDesktop;
  const desktopBasemapApiRoot = useMemo(() => getDesktopBasemapApiRoot(), []);
  const basemapUrl = useMemo(() => {
    if (isDesktopApp && desktopBasemapApiRoot) {
      return `${desktopBasemapApiRoot}/api/basemap/${selectedBasemap.id}/{z}/{x}/{y}.png`;
    }
    return selectedBasemap.url;
  }, [desktopBasemapApiRoot, isDesktopApp, selectedBasemap]);
  const [isBasemapAvailable, setIsBasemapAvailable] = useState(true);
  const center = [51.0511, 16.1935]; // Jawor, Polska
  const zoom = 11;
  const loadingMapIcon = useMemo(
    () =>
      L.divIcon({
        className: 'map-loading-marker-wrapper',
        html: `<div class="map-loading-marker"><span class="map-loading-pin">●</span><div class="map-loading-content"><div class="map-loading-track"><div class="map-loading-fill" style="width:${Math.max(
          0,
          Math.min(100, uploadProgressPercent)
        )}%"></div></div><div class="map-loading-meta">${escapeHtml(uploadProgress || t('upload.progress.loading'))}</div></div><span class="map-loading-percent">${Math.round(uploadProgressPercent)}%</span></div>`,
        iconSize: [280, 46],
        iconAnchor: [20, 17],
      }),
    [uploadProgress, uploadProgressPercent, t]
  );

  const damageAssessment = useMemo(() => {
    if (!aoiGeometry) return null;

    try {
      const result = calculateDamageAssessment({
        aoiGeometry,
        spots: cropItems.map((item) => ({
          geometry: item.geometry,
          damagePercent: Number.isFinite(Number(cropDamagePercents[item.id]))
            ? Number(cropDamagePercents[item.id])
            : 100,
        })),
        exclusions: exclusionItems.map((item) => ({ geometry: item.geometry })),
        remainingFieldDamagePercent,
      });
      return { ...result, errorCode: null };
    } catch (err) {
      return { errorCode: err?.code || 'invalid-geometry' };
    }
  }, [aoiGeometry, cropItems, cropDamagePercents, exclusionItems, remainingFieldDamagePercent]);

  const totalDamageHa = damageAssessment?.errorCode ? NaN : Number(damageAssessment?.totalDamageHa || 0);
  const affectedPercent = damageAssessment?.errorCode
    ? NaN
    : Math.round(Number(damageAssessment?.totalDamagePercent || 0) * 10) / 10;
  // Net 100% base = gross AOI minus exclusions (green islands).
  const netAoiAreaHa = damageAssessment?.errorCode
    ? NaN
    : Number(damageAssessment?.aoiAreaHa ?? aoiAreaHa);
  const exclusionAreaHa = damageAssessment?.errorCode ? NaN : Number(damageAssessment?.exclusionAreaHa || 0);

  useEffect(() => {
    exclusionDrawModeRef.current = exclusionDrawMode;
  }, [exclusionDrawMode]);

  useEffect(() => {
    setIsBasemapAvailable(true);
  }, [basemapUrl]);

  useEffect(
    () => () => {
      window.clearTimeout(cropHighlightTimerRef.current);
      cropPulseTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      cropPulseTimersRef.current.clear();
    },
    []
  );

  useEffect(() => {
    const unsubscribeDesktopShortcut = window.desktopApi?.onToggleDeveloperTools?.(() => {
      setDeveloperToolsEnabled((enabled) => !enabled);
    });
    if (unsubscribeDesktopShortcut) {
      return unsubscribeDesktopShortcut;
    }

    const handleDeveloperToolsShortcut = (event) => {
      if (event.ctrlKey && event.shiftKey && event.altKey && event.code === 'KeyC') {
        event.preventDefault();
        setDeveloperToolsEnabled((enabled) => !enabled);
      }
    };

    window.addEventListener('keydown', handleDeveloperToolsShortcut);
    return () => window.removeEventListener('keydown', handleDeveloperToolsShortcut);
  }, []);

  const resetSelectionState = () => {
    setSelectedPoint(null);
    setAoiGeometry(null);
    setAoiAreaHa(null);
    setCropItems([]);
    setCropDamagePercents({});
    setCropProtectedSpecies({});
    setExclusionItems([]);
    setExclusionDrawMode(false);
    setRemainingFieldDamagePercent(0);
    aoiLayerIdRef.current = null;
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
  };

  const beginNewReportDraft = () => {
    if (activeReportId === null) {
      return;
    }

    setActiveReportId(null);
  };

  const deriveFieldNameFromFilename = (filename) => {
    if (!filename) {
      return t('field.defaultName', { date: new Date().toLocaleString('pl-PL') });
    }

    const withoutExtension = filename.replace(/\.[^.]+$/, '');
    const normalized = withoutExtension.replace(/[_-]+/g, ' ').trim();
    return normalized || t('field.defaultName', { date: new Date().toLocaleString('pl-PL') });
  };

  const computeGeometryAreaHa = (geoJsonGeometry) => {
    if (!geoJsonGeometry) return 0;
    const squareMeters = area({ type: 'Feature', geometry: geoJsonGeometry, properties: {} });
    return squareMeters / 10000;
  };

  // Bbox center falls outside concave shapes (e.g. an L-shaped spot); pointOnFeature guarantees a point on the actual polygon.
  const computeFlagAnchorPoint = (geoJsonGeometry, fallbackLayer) => {
    if (geoJsonGeometry) {
      try {
        const point = pointOnFeature({ type: 'Feature', geometry: geoJsonGeometry, properties: {} });
        const [lng, lat] = point?.geometry?.coordinates || [];
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng };
        }
      } catch (err) {
        // fall through to bounds-based fallback below
      }
    }

    if (fallbackLayer) {
      const center = fallbackLayer.getBounds().getCenter();
      return { lat: center.lat, lng: center.lng };
    }

    return null;
  };

  const generateCropId = () => (
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `crop-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );

  const getUploadErrorMessage = (err, file) => {
    const status = err?.response?.status;
    const backendMessage = err?.response?.data?.message;

    if (status === 413) {
      const sizeMb = file?.size ? (file.size / (1024 * 1024)).toFixed(1) : null;
      const sizeInfo = sizeMb ? t('upload.error.fileSize', { sizeMb }) : '';
      return t('upload.error.tooBig', { sizeInfo });
    }

    return t('upload.error.generic', { message: backendMessage || err.message });
  };

  const setProgressState = (message, percent) => {
    setUploadProgress(message);
    setUploadProgressPercent(percent);
  };

  const getLoadingIndicatorPoint = (fallbackPoint = null) => {
    if (fallbackPoint && Number.isFinite(Number(fallbackPoint.lat)) && Number.isFinite(Number(fallbackPoint.lng))) {
      return { lat: Number(fallbackPoint.lat), lng: Number(fallbackPoint.lng) };
    }

    if (selectedPoint) {
      return selectedPoint;
    }

    const mapCenter = mapRef.current?.getCenter?.();
    if (mapCenter && Number.isFinite(mapCenter.lat) && Number.isFinite(mapCenter.lng)) {
      return { lat: mapCenter.lat, lng: mapCenter.lng };
    }

    return { lat: center[0], lng: center[1] };
  };

  const animateToBaseViewport = (targetPoint = null) => {
    if (!mapRef.current) {
      return Promise.resolve();
    }

    const map = mapRef.current;

    return new Promise((resolve) => {
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        map.off('moveend', handleMoveEnd);
        window.clearTimeout(timeoutId);
        resolve();
      };

      const handleMoveEnd = () => {
        finish();
      };

      const timeoutId = window.setTimeout(finish, 1100);
      map.on('moveend', handleMoveEnd);

      const hasTarget =
        targetPoint &&
        Number.isFinite(Number(targetPoint.lat)) &&
        Number.isFinite(Number(targetPoint.lng));

      const destination = hasTarget
        ? [Number(targetPoint.lat), Number(targetPoint.lng)]
        : center;

      map.flyTo(destination, zoom, { animate: true, duration: 0.75 });
    });
  };

  const animateToBounds = (bounds, options = {}) => {
    if (!mapRef.current || !bounds) {
      return;
    }
    const leafletBounds = L.latLngBounds(bounds);
    if (!leafletBounds.isValid()) {
      return;
    }
    mapRef.current.stop();
    mapRef.current.flyToBounds(leafletBounds, options);
  };

  const recomputeDrawingState = () => {
    if (!featureGroupRef.current) return;

    let currentAoiGeometry = null;
    let currentAoiArea = 0;
    const currentCropItems = [];
    const currentExclusionItems = [];
    let currentAoiCenter = null;

    featureGroupRef.current.eachLayer((layer) => {
      const geometry = layer.toGeoJSON().geometry;
      if (layer.workflowType === 'aoi') {
        currentAoiGeometry = geometry;
        currentAoiArea = computeGeometryAreaHa(geometry);
        currentAoiCenter = layer.getBounds().getCenter();
      } else if (layer.workflowType === 'exclusion') {
        if (!layer.exclusionId) {
          layer.exclusionId = generateCropId();
        }
        currentExclusionItems.push({
          id: layer.exclusionId,
          leafletLayerId: layer._leaflet_id,
          geometry,
          areaHa: computeGeometryAreaHa(geometry),
        });
      } else if (layer.workflowType === 'crop') {
        if (!layer.cropId) {
          layer.cropId = generateCropId();
        }
        if (!Number.isInteger(layer.cropNumber) || layer.cropNumber < 1) {
          layer.cropNumber = assignNextSpotNumber(
            reports.find((report) => String(report.id) === String(activeReportId))
          );
        }
        currentCropItems.push({
          id: layer.cropId,
          spotNumber: layer.cropNumber,
          leafletLayerId: layer._leaflet_id,
          geometry,
          areaHa: computeGeometryAreaHa(geometry),
          center: computeFlagAnchorPoint(geometry, layer),
        });
      }
    });

    setAoiGeometry(currentAoiGeometry);
    setAoiAreaHa(currentAoiArea || null);
    setCropItems(currentCropItems);
    setExclusionItems(currentExclusionItems);
    setCropDamagePercents((prevPercents) => {
      const nextPercents = {};
      currentCropItems.forEach((item) => {
        const existing = Number(prevPercents[item.id]);
        nextPercents[item.id] = Number.isFinite(existing) ? existing : 100;
      });
      return nextPercents;
    });
    setCropProtectedSpecies((prevFlags) => {
      const nextFlags = {};
      currentCropItems.forEach((item) => {
        nextFlags[item.id] = Boolean(prevFlags[item.id]);
      });
      return nextFlags;
    });

    if (currentAoiCenter) {
      setSelectedPoint({ lat: currentAoiCenter.lat, lng: currentAoiCenter.lng });
    } else {
      setSelectedPoint(null);
    }
  };

  const handleUseWholeGeoTIFFAsAoi = async () => {
    if (!canEditFields || uploading) {
      return;
    }

    if (!geoTIFFData?.bbox || !Array.isArray(geoTIFFData.bbox) || geoTIFFData.bbox.length !== 4) {
      setReportsError(t('field.error.needGeotiff'));
      return;
    }

    if (!featureGroupRef.current) {
      setReportsError(t('field.error.mapNotReady'));
      return;
    }

    setUploading(true);
    setReportsError('');
    setProgressState(t('map.geotiff.preparingAoi'), 50);
    try {
      const result = await getGeoTIFFAoi(geoTIFFData.filename);
      const aoiLayer = L.geoJSON(result.geometry, {
        style: { color: '#1e88e5', weight: 3, fill: false, fillOpacity: 0 },
      }).getLayers()[0];
      if (!(aoiLayer instanceof L.Polygon) || !aoiLayer.getBounds().isValid()) {
        throw new Error(t('field.error.boundsFailed'));
      }
      resetSelectionState();
      aoiLayer.workflowType = 'aoi';
      featureGroupRef.current.addLayer(aoiLayer);
      aoiLayerIdRef.current = aoiLayer._leaflet_id;
      recomputeDrawingState();
      animateToBounds(aoiLayer.getBounds(), { padding: [40, 40], maxZoom: MAX_INTERACTION_ZOOM, duration: 0.8 });
    } catch (err) {
      console.error('Error preparing automatic AOI:', err);
      setReportsError(t('field.error.aoiFailed', { message: err?.response?.data?.message || err.message }));
    } finally {
      setUploading(false);
      setProgressState('', 0);
    }
  };

  const handleToggleExclusionMode = () => {
    if (!canEditFields || !aoiGeometry) {
      return;
    }
    setReportsError('');
    setExclusionDrawMode((enabled) => !enabled);
  };

  const handleDeleteExclusion = (exclusionId) => {
    if (!canEditFields || !featureGroupRef.current) {
      return;
    }
    featureGroupRef.current.eachLayer((layer) => {
      if (layer.workflowType === 'exclusion' && layer.exclusionId === exclusionId) {
        featureGroupRef.current.removeLayer(layer);
      }
    });
    recomputeDrawingState();
    setReportsError('');
  };

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const existingReports = await getFields();
        setReports(existingReports);
        if (existingReports.length > 0) {
          await reportRestorerRef.current?.(existingReports[0]);
        }
      } catch (err) {
        console.error('Blad ladowania raportow:', err);
        setReportsError(err?.response?.data?.message || err.message);
      } finally {
        setLoadingReports(false);
      }
    };

    fetchReports();
  }, []);

  const handleFileSelect = async (file, inspectionDamagePercent = null) => {
    if (!canEditFields) {
      return;
    }

    setError('');
    setReportsError('');
    setProgressState('', 0);

    try {
      if (activeReportId) {
        await saveReportState(activeReportId, { silent: true });
      }

      beginNewReportDraft();
      setGeoTIFFData(null);
      resetSelectionState();
      setLoadingIndicatorPoint(getLoadingIndicatorPoint());
      setUploading(true);

      setProgressState(t('upload.progress.sending'), 20);

      const uploadResult = await uploadGeoTIFF(file);

      setProgressState(
        t('upload.progress.compression', {
          original: uploadResult.compression_info.original_size_mb,
          compressed: uploadResult.compression_info.compressed_size_mb,
        }),
        55
      );
      setUploadedFilename(uploadResult.filename);

      setProgressState(t('upload.progress.rendering'), 78);
      const data = await getGeoTIFFMetadata(uploadResult.filename);
      setGeoTIFFData(data);
      const [west, south, east, north] = data.bbox;
      animateToBounds([[south, west], [north, east]], { padding: [50, 50], maxZoom: MAX_INTERACTION_ZOOM });

      const referencePoint = getGeoTiffCenterLatLng(data);
      if (!referencePoint) {
        throw new Error(t('field.error.locationFailed'));
      }

      setProgressState(t('upload.progress.creatingField'), 97);
      setSavingReport(true);
      const newField = await createField({
        title: deriveFieldNameFromFilename(uploadResult.filename),
        latitude: referencePoint.lat,
        longitude: referencePoint.lng,
        inspection_damage_percent: inspectionDamagePercent !== null
          && inspectionDamagePercent !== ''
          && Number.isFinite(Number(inspectionDamagePercent))
          ? Number(inspectionDamagePercent)
          : null,
        notes: '',
        geotiff_filename: uploadResult.filename,
        geotiff_metadata: {
          ...(data?.metadata || {}),
          source_filename: uploadResult.filename,
        },
      });

      setReports((prevReports) => [newField, ...prevReports]);
      setActiveReportId(newField.id);
      setProgressState(t('upload.progress.done'), 100);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 413 || err?.response?.data) {
        setError(getUploadErrorMessage(err, file));
      } else {
        setReportsError(err.message || t('field.error.createFailed'));
      }
      setGeoTIFFData(null);
    } finally {
      setProgressState('', 0);
      setUploading(false);
      setSavingReport(false);
      setLoadingIndicatorPoint(null);
    }
  };

  const handleShapeCreated = (event) => {
    if (!drawingEnabled) {
      if (featureGroupRef.current && event?.layer) {
        featureGroupRef.current.removeLayer(event.layer);
      }
      return;
    }

    const { layer } = event;

    if (!aoiLayerIdRef.current) {
      layer.workflowType = 'aoi';
      layer.setStyle(AOI_LAYER_STYLE);
      aoiLayerIdRef.current = layer._leaflet_id;
      setReportsError('');
      recomputeDrawingState();
      return;
    }

    const geometry = layer.toGeoJSON().geometry;

    if (exclusionDrawModeRef.current) {
      try {
        validateDamageGeometries(aoiGeometry, cropItems, [...exclusionItems, { geometry }]);
      } catch (err) {
        if (featureGroupRef.current) {
          featureGroupRef.current.removeLayer(layer);
        }
        setReportsError(t('field.error.exclusionInside'));
        return;
      }

      layer.workflowType = 'exclusion';
      layer.exclusionId = generateCropId();
      layer.setStyle(EXCLUSION_LAYER_STYLE);
      setReportsError('');
      recomputeDrawingState();
      return;
    }

    try {
      validateDamageGeometries(aoiGeometry, [...cropItems, { geometry }], exclusionItems);
    } catch (err) {
      if (featureGroupRef.current) {
        featureGroupRef.current.removeLayer(layer);
      }
      setReportsError(t(`field.error.${err?.code === 'overlapping-spots' ? 'cropsOverlap' : 'cropInside'}`));
      return;
    }

    layer.workflowType = 'crop';
    layer.cropId = generateCropId();
    layer.cropNumber = assignNextSpotNumber(reports.find((report) => String(report.id) === String(activeReportId)));
    layer.setStyle(CROP_LAYER_BASE_STYLE);
    if (activeReportId) {
      bindCropLayerInteractions(layer, layer.cropId, activeReportId);
    }
    setReportsError('');
    recomputeDrawingState();
  };

  const handleShapesEdited = (event) => {
    if (!canEditFields) {
      return;
    }

    event.layers.eachLayer((layer) => {
      if (layer.workflowType === 'aoi') {
        layer.setStyle(AOI_LAYER_STYLE);
      }
      if (layer.workflowType === 'crop') {
        layer.setStyle(CROP_LAYER_BASE_STYLE);
      }
      if (layer.workflowType === 'exclusion') {
        layer.setStyle(EXCLUSION_LAYER_STYLE);
      }
    });

    recomputeDrawingState();

    const geometries = [];
    const exclusionGeometries = [];
    let currentAoiGeometry = null;
    featureGroupRef.current?.eachLayer((layer) => {
      const geometry = layer.toGeoJSON().geometry;
      if (layer.workflowType === 'aoi') currentAoiGeometry = geometry;
      if (layer.workflowType === 'crop') geometries.push({ geometry });
      if (layer.workflowType === 'exclusion') exclusionGeometries.push({ geometry });
    });

    try {
      validateDamageGeometries(currentAoiGeometry, geometries, exclusionGeometries);
      setReportsError('');
    } catch (err) {
      const errorKey = err?.code === 'overlapping-spots'
        ? 'cropsOverlap'
        : err?.code === 'exclusion-outside-aoi'
          ? 'exclusionInside'
          : 'cropsInsideAfterEdit';
      setReportsError(t(`field.error.${errorKey}`));
    }
  };

  const handleShapesDeleted = (event) => {
    if (!canEditFields) {
      return;
    }

    if (!featureGroupRef.current) return;

    const deletedCropIds = new Set();
    const deletedExclusionIds = new Set();
    const deletedLayerIds = new Set();
    let deletedAoi = false;

    event?.layers?.eachLayer((layer) => {
      if (layer._leaflet_id === aoiLayerIdRef.current || layer.workflowType === 'aoi') {
        deletedAoi = true;
      }
      if (layer.cropId) {
        deletedCropIds.add(layer.cropId);
      }
      if (layer.exclusionId) {
        deletedExclusionIds.add(layer.exclusionId);
      }
      if (layer._leaflet_id) {
        deletedLayerIds.add(layer._leaflet_id);
      }
    });

    if (deletedAoi) {
      resetSelectionState();
      setReportsError('');
      return;
    }

    const removedIdSet = new Set(cropItems
      .filter((item) => deletedCropIds.has(item.id) || deletedLayerIds.has(item.leafletLayerId))
      .map((item) => item.id));
    if (removedIdSet.size > 0) {
      setCropItems((previousItems) => previousItems.filter((item) => !removedIdSet.has(item.id)));
      setCropDamagePercents((previousPercents) => Object.fromEntries(
        Object.entries(previousPercents).filter(([cropId]) => !removedIdSet.has(cropId))
      ));
      setCropProtectedSpecies((previousFlags) => Object.fromEntries(
        Object.entries(previousFlags).filter(([cropId]) => !removedIdSet.has(cropId))
      ));
    }

    const removedExclusionSet = new Set(exclusionItems
      .filter((item) => deletedExclusionIds.has(item.id) || deletedLayerIds.has(item.leafletLayerId))
      .map((item) => item.id));
    if (removedExclusionSet.size > 0) {
      setExclusionItems((previousItems) => previousItems.filter((item) => !removedExclusionSet.has(item.id)));
    }
    setReportsError('');
  };

  drawingHandlersRef.current = {
    onCreated: handleShapeCreated,
    onEdited: handleShapesEdited,
    onDeleted: handleShapesDeleted,
  };

  const formatCoord = (value) => (Number.isFinite(Number(value)) ? Number(value).toFixed(6) : t('common.noData'));

  const formatArea = (valueHa) => {
    if (!Number.isFinite(Number(valueHa))) return t('common.noData');

    const areaHa = Number(valueHa);
    const areaAres = areaHa * 100;
    return t('field.areaFormat', { ha: Number(areaHa.toFixed(4)), ares: Number(areaAres.toFixed(2)) });
  };

  const createLayerFromGeometry = (geometry, workflowType, style) => {
    if (!geometry || !featureGroupRef.current) return [];

    const group = L.geoJSON(geometry, { style });
    const layers = [];
    group.eachLayer((layer) => {
      layer.workflowType = workflowType;
      featureGroupRef.current.addLayer(layer);
      layers.push(layer);
    });
    return layers;
  };

  const saveReportState = async (reportId, { silent = false } = {}) => {
    if (!reportId) {
      return null;
    }

    if (aoiGeometry && damageAssessment?.errorCode) {
      const errorKey = damageAssessment?.errorCode === 'overlapping-spots' ? 'cropsOverlap' : 'invalidDamageGeometry';
      const calculationError = new Error(t(`field.error.${errorKey}`));
      if (!silent) setReportsError(calculationError.message);
      throw calculationError;
    }

    const payload = {
      aoi_geometry: aoiGeometry || null,
      crop_geometries: cropItems.map((item) => serializeCropGeometryEntry({
        id: item.id,
        spotNumber: item.spotNumber,
        geometry: item.geometry,
        damagePercent: Number.isFinite(Number(cropDamagePercents[item.id])) ? Number(cropDamagePercents[item.id]) : 100,
        protectedSpecies: Boolean(cropProtectedSpecies[item.id]),
      })),
      exclusion_geometries: exclusionItems.map((item) => ({ id: item.id, geometry: item.geometry })),
      aoi_area_ha: Number.isFinite(Number(aoiAreaHa)) ? Number(aoiAreaHa) : null,
      exclusion_area_ha: Number.isFinite(exclusionAreaHa) ? exclusionAreaHa : 0,
      spot_union_area_ha: damageAssessment?.spotUnionAreaHa ?? null,
      remaining_area_ha: damageAssessment?.remainingAreaHa ?? null,
      spot_damage_area_ha: damageAssessment?.spotDamageHa ?? null,
      remaining_damage_area_ha: damageAssessment?.remainingDamageHa ?? null,
      remaining_field_damage_percent: Number(remainingFieldDamagePercent),
      next_crop_number: getNextSpotNumber(reports.find((report) => String(report.id) === String(reportId))),
      affected_area_ha: Number.isFinite(totalDamageHa) ? totalDamageHa : null,
      affected_percent: Number.isFinite(Number(affectedPercent)) ? Number(affectedPercent) : 0,
    };

    try {
      const updatedReport = await updateField(reportId, payload);
      setReports((prevReports) => prevReports.map((report) => (report.id === reportId ? updatedReport : report)));
      return updatedReport;
    } catch (err) {
      if (!silent) {
        const backendMessage = err?.response?.data?.message;
        setReportsError(backendMessage || err.message || t('field.error.saveStateFailed'));
      }
      throw err;
    }
  };

  const applyReportGeometryToMap = (report, { fitToBounds = true } = {}) => {
    resetSelectionState();
    if (!featureGroupRef.current) return;

    const aoiGeometry = report.aoi_geometry || report.geometry || null;
    const aoiLayers = createLayerFromGeometry(aoiGeometry, 'aoi', AOI_LAYER_STYLE);
    if (aoiLayers.length > 0) {
      aoiLayerIdRef.current = aoiLayers[0]._leaflet_id;
    }

    const exclusionGeometriesFromReport = Array.isArray(report.exclusion_geometries) ? report.exclusion_geometries : [];
    exclusionGeometriesFromReport.forEach((item, index) => {
      const geometry = item?.geometry || (item?.type ? item : null);
      if (!geometry) return;
      const exclusionId = item?.id || `exclusion-${report.id}-${index}`;
      const exclusionLayers = createLayerFromGeometry(geometry, 'exclusion', EXCLUSION_LAYER_STYLE);
      exclusionLayers.forEach((layer) => {
        layer.exclusionId = exclusionId;
      });
    });

    const cropGeometriesFromReport = Array.isArray(report.crop_geometries) ? report.crop_geometries : [];
    const initialDamagePercents = {};
    const initialProtectedSpecies = {};
    cropGeometriesFromReport.forEach((item, index) => {
      const { geometry, id, spotNumber, damagePercent, protectedSpecies } = normalizeCropGeometryEntry(
        item,
        null,
        index + 1
      );
      const cropLayers = createLayerFromGeometry(geometry, 'crop', CROP_LAYER_BASE_STYLE);
      cropLayers.forEach((layer) => {
        layer.cropId = id;
        layer.cropNumber = spotNumber;
        bindCropLayerInteractions(layer, id, report.id);
      });
      initialDamagePercents[id] = damagePercent;
      initialProtectedSpecies[id] = Boolean(protectedSpecies);
    });
    getNextSpotNumber(report);
    setCropDamagePercents(initialDamagePercents);
    setCropProtectedSpecies(initialProtectedSpecies);
    setRemainingFieldDamagePercent(Number.isFinite(Number(report.remaining_field_damage_percent))
      ? Number(report.remaining_field_damage_percent)
      : 0);

    recomputeDrawingState();

    const bounds = featureGroupRef.current.getBounds();
    if (fitToBounds) {
      animateToBounds(bounds, { padding: [40, 40], maxZoom: MAX_INTERACTION_ZOOM, duration: 0.8 });
    }
  };

  const handleRestoreReport = async (report, { preserveViewport = false } = {}) => {
    if (uploading || restoringReportRef.current) return;

    if (String(activeReportId) === String(report.id)) {
      if (!preserveViewport) {
        let bounds = featureGroupRef.current?.getBounds();
        if (!bounds?.isValid() && geoTIFFData?.bbox) {
          const [west, south, east, north] = geoTIFFData.bbox;
          bounds = L.latLngBounds([[south, west], [north, east]]);
        }
        animateToBounds(bounds, {
          padding: [40, 40], maxZoom: MAX_INTERACTION_ZOOM, duration: 0.8,
        });
      }
      return;
    }

    restoringReportRef.current = true;
    try {
      setReportsError('');
      setUploading(true);

      if (canEditFields && activeReportId && String(activeReportId) !== String(report.id)) {
        await saveReportState(activeReportId);
      }

      const reportPoint = getLoadingIndicatorPoint({ lat: report.latitude, lng: report.longitude });
      setLoadingIndicatorPoint(reportPoint);
      setProgressState(t('upload.progress.movingToField'), 12);

      if (!preserveViewport) {
        await animateToBaseViewport(reportPoint);
      }

      const reportGeoTiffFilename = report.geotiff_filename || report.geotiff_metadata?.source_filename || '';
      let restoredGeoTiffData = null;

      if (reportGeoTiffFilename) {
        setProgressState(t('upload.progress.loadingFromReport', { filename: reportGeoTiffFilename }), 36);
        const data = await getGeoTIFFMetadata(reportGeoTiffFilename);
        restoredGeoTiffData = data;
      }

      setActiveReportId(report.id);
      setGeoTIFFData(restoredGeoTiffData);
      setUploadedFilename(reportGeoTiffFilename);
      setProgressState(t('upload.progress.movingToSelected'), 92);
      applyReportGeometryToMap(report, { fitToBounds: !preserveViewport });
      if (restoredGeoTiffData && !featureGroupRef.current?.getBounds().isValid() && !preserveViewport) {
        const [west, south, east, north] = restoredGeoTiffData.bbox;
        animateToBounds([[south, west], [north, east]], { padding: [50, 50], maxZoom: MAX_INTERACTION_ZOOM });
      }
      setProgressState(t('upload.progress.done'), 100);
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      const message = err.code === 'ECONNABORTED'
        ? t('field.error.loadTimeout')
        : backendMessage || err.message;
      setReportsError(t('field.error.restoreFailed', { message }));
    } finally {
      restoringReportRef.current = false;
      setProgressState('', 0);
      setUploading(false);
      setLoadingIndicatorPoint(null);
    }
  };

  reportRestorerRef.current = handleRestoreReport;

  const handleDownloadPdfReport = async (report) => {
    if (!report || !mapRef.current || String(activeReportId) !== String(report.id)) {
      return;
    }

    try {
      setReportsError('');
      if (!aoiGeometry || damageAssessment?.errorCode) {
        throw new Error(t('field.error.invalidDamageGeometry'));
      }
      setPdfExportReportId(report.id);
      const map = mapRef.current;
      const reportBounds = featureGroupRef.current?.getBounds?.();
      if (reportBounds?.isValid?.()) {
        map.invalidateSize({ pan: false });
        map.fitBounds(reportBounds, { padding: [36, 36], maxZoom: MAX_INTERACTION_ZOOM, animate: false });
        await new Promise((resolve) => window.setTimeout(resolve, 350));
      }

      const mapCanvas = await import('html2canvas').then(({ default: html2canvas }) => html2canvas(map.getContainer(), {
        backgroundColor: '#f8fafc',
        logging: false,
        scale: 1,
        useCORS: true,
        onclone: (clonedDocument) => {
          clonedDocument.querySelectorAll('.leaflet-control-container').forEach((element) => element.remove());
        },
      }));
      const activeReportSnapshot = {
        ...report,
        aoi_area_ha: aoiAreaHa,
        exclusion_area_ha: Number.isFinite(exclusionAreaHa) ? exclusionAreaHa : 0,
        net_aoi_area_ha: Number.isFinite(netAoiAreaHa) ? netAoiAreaHa : aoiAreaHa,
        exclusion_count: exclusionItems.length,
        spot_union_area_ha: damageAssessment?.spotUnionAreaHa ?? report.spot_union_area_ha,
        remaining_area_ha: damageAssessment?.remainingAreaHa ?? report.remaining_area_ha,
        spot_damage_area_ha: damageAssessment?.spotDamageHa ?? report.spot_damage_area_ha,
        remaining_damage_area_ha: damageAssessment?.remainingDamageHa ?? report.remaining_damage_area_ha,
        remaining_field_damage_percent: remainingFieldDamagePercent,
        affected_area_ha: totalDamageHa,
        affected_percent: affectedPercent,
        crop_geometries: cropItems.map((item) => serializeCropGeometryEntry({
          id: item.id,
          spotNumber: item.spotNumber,
          geometry: item.geometry,
          damagePercent: cropDamagePercents[item.id],
          protectedSpecies: cropProtectedSpecies[item.id],
        })),
      };
      await downloadPdfReport({ report: activeReportSnapshot, mapImage: mapCanvas.toDataURL('image/png') });
    } catch (err) {
      setReportsError(`Nie udało się wygenerować PDF: ${err.message}`);
    } finally {
      setPdfExportReportId(null);
    }
  };

  const handleSaveReportState = async (reportId) => {
    try {
      setReportsError('');
      setReportActionId(reportId);
      await saveReportState(reportId);
    } finally {
      setReportActionId(null);
    }
  };

  const startEditingReportTitle = (report) => {
    setReportsError('');
    setEditingReportId(report.id);
    setEditingReportTitle(report.title || '');
  };

  const cancelEditingReportTitle = () => {
    setEditingReportId(null);
    setEditingReportTitle('');
  };

  const startEditingReportNotes = (report) => {
    setReportsError('');
    setEditingReportNotesId(report.id);
    setEditingReportNotes(report.notes || '');
  };

  const cancelEditingReportNotes = () => {
    setEditingReportNotesId(null);
    setEditingReportNotes('');
  };

  const handleSaveReportTitle = async (reportId) => {
    const nextTitle = editingReportTitle.trim();
    if (!nextTitle) {
      setReportsError(t('field.error.nameEmpty'));
      return;
    }

    try {
      setReportsError('');
      setReportActionId(reportId);
      const updatedReport = await updateField(reportId, { title: nextTitle });
      setReports((prevReports) => prevReports.map((report) => (report.id === reportId ? updatedReport : report)));
      cancelEditingReportTitle();
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      setReportsError(backendMessage || err.message || t('field.error.renameFailed'));
    } finally {
      setReportActionId(null);
    }
  };

  const handleSaveReportNotes = async (reportId) => {
    try {
      setReportsError('');
      setReportActionId(reportId);
      const updatedReport = await updateField(reportId, { notes: editingReportNotes.trim() });
      setReports((prevReports) => prevReports.map((report) => (report.id === reportId ? updatedReport : report)));
      cancelEditingReportNotes();
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      setReportsError(backendMessage || err.message || t('field.error.notesFailed'));
    } finally {
      setReportActionId(null);
    }
  };

  const handleSaveFieldDetails = async (event, reportId) => {
    event.preventDefault();
    if (!canEditFields) {
      return;
    }

    const formData = new FormData(event.currentTarget);

    try {
      setReportsError('');
      setReportActionId(reportId);
      const updatedReport = await updateField(reportId, {
        crop_type: String(formData.get('cropType') || ''),
        owner_name: String(formData.get('ownerName') || '').trim(),
      });
      setReports((prevReports) => prevReports.map((report) => (report.id === reportId ? updatedReport : report)));
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      setReportsError(backendMessage || err.message || t('field.error.detailsFailed'));
    } finally {
      setReportActionId(null);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!canEditFields) {
      return;
    }

    const reportToDelete = reports.find((report) => report.id === reportId);
    const shouldDelete = window.confirm(t('field.confirmDelete', { title: reportToDelete?.title || reportId }));
    if (!shouldDelete) {
      return;
    }

    try {
      setReportsError('');
      setReportActionId(reportId);
      await deleteField(reportId);
      setReports((prevReports) => prevReports.filter((report) => report.id !== reportId));
      if (activeReportId === reportId) {
        beginNewReportDraft();
        setGeoTIFFData(null);
        setUploadedFilename('');
        resetSelectionState();
      }
      if (editingReportId === reportId) {
        cancelEditingReportTitle();
      }
      if (editingReportNotesId === reportId) {
        cancelEditingReportNotes();
      }
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      setReportsError(backendMessage || err.message || t('field.error.deleteFailed'));
    } finally {
      setReportActionId(null);
    }
  };

  const handleMapReady = useCallback((mapInstance) => {
    mapRef.current = mapInstance;
  }, []);

  const handleCropDamagePercentChange = (cropId, rawValue) => {
    if (rawValue === '') {
      setCropDamagePercents((prev) => ({ ...prev, [cropId]: '' }));
      return;
    }

    const numeric = Number(rawValue);
    if (!Number.isFinite(numeric)) return;

    const clamped = Math.max(0, Math.min(100, numeric));
    setCropDamagePercents((prev) => ({ ...prev, [cropId]: clamped }));
  };

  const handleCropProtectedSpeciesChange = (cropId, nextValue) => {
    setCropProtectedSpecies((prev) => ({ ...prev, [cropId]: Boolean(nextValue) }));
  };

  const handleCropLayerDeleted = (cropId) => {
    setCropItems((previousItems) => previousItems.filter((item) => item.id !== cropId));
    setCropDamagePercents((previousPercents) => Object.fromEntries(
      Object.entries(previousPercents).filter(([existingCropId]) => existingCropId !== cropId)
    ));
    setCropProtectedSpecies((previousFlags) => Object.fromEntries(
      Object.entries(previousFlags).filter(([existingCropId]) => existingCropId !== cropId)
    ));
    setReportsError('');
  };

  const handleDeleteCropSpot = (cropId) => {
    if (!canEditFields || !featureGroupRef.current) {
      return;
    }

    featureGroupRef.current.eachLayer((layer) => {
      if (layer.workflowType === 'crop' && layer.cropId === cropId) {
        featureGroupRef.current.removeLayer(layer);
      }
    });
    recomputeDrawingState();
    setReportsError('');
  };

  const handleRemainingFieldDamagePercentChange = (rawValue) => {
    if (rawValue === '') {
      setRemainingFieldDamagePercent('');
      return;
    }

    const numeric = Number(rawValue);
    if (!Number.isFinite(numeric)) return;
    setRemainingFieldDamagePercent(Math.max(0, Math.min(100, numeric)));
  };

  // Dla aktywnego pola korzysta z rysowanych warstw (edytowalne); dla pozostalych z zapisanych danych (podglad).
  const getCropDisplayItems = (report, isActiveReport) => {
    if (isActiveReport) {
      return cropItems.map((item, index) => {
        const rawPercent = Number(cropDamagePercents[item.id]);
        const damagePercent = Number.isFinite(rawPercent) ? rawPercent : 100;
        return {
          id: item.id,
          spotNumber: item.spotNumber,
          geometry: item.geometry,
          areaHa: damageAssessment?.errorCode
            ? NaN
            : (damageAssessment?.effectiveSpotAreasHa?.[index] ?? item.areaHa),
          damagePercent,
          protectedSpecies: Boolean(cropProtectedSpecies[item.id]),
          editable: canEditFields,
        };
      });
    }

    const savedCrops = Array.isArray(report.crop_geometries) ? report.crop_geometries : [];
    const savedItems = savedCrops.map((item, index) => {
      const normalized = normalizeCropGeometryEntry(item, `saved-${report.id}-${index}`, index + 1);
      return {
        id: normalized.id,
        spotNumber: normalized.spotNumber,
        geometry: normalized.geometry,
        areaHa: computeGeometryAreaHa(normalized.geometry),
        damagePercent: normalized.damagePercent,
        protectedSpecies: Boolean(normalized.protectedSpecies),
        editable: false,
      };
    });
    if (!report.exclusion_geometries?.length || savedItems.length === 0) {
      return savedItems;
    }
    try {
      const assessment = calculateDamageAssessment({
        aoiGeometry: report.aoi_geometry || report.geometry,
        spots: savedItems,
        exclusions: report.exclusion_geometries,
      });
      return savedItems.map((item, index) => ({
        ...item,
        areaHa: assessment.effectiveSpotAreasHa[index],
      }));
    } catch (err) {
      return savedItems.map((item) => ({ ...item, areaHa: NaN, calculationError: true }));
    }
  };

  const getCropListKey = (reportId, cropId) => `${reportId}:${cropId}`;

  const revealCropInList = (reportId, cropId) => {
    const cropKey = getCropListKey(reportId, cropId);
    const listItem = cropListItemRefs.current.get(cropKey);
    listItem?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedCropKey(cropKey);
    window.clearTimeout(cropHighlightTimerRef.current);
    cropHighlightTimerRef.current = window.setTimeout(() => setHighlightedCropKey(null), 1100);
  };

  const bindCropLayerInteractions = (layer, cropId, reportId) => {
    if (!layer || !cropId || !reportId) {
      return;
    }

    layer.off('click');
    layer.off('mouseover');
    layer.off('mouseout');
    layer.off('deleted');
    layer.off('revert-deleted');

    const getMapContainer = () => mapRef.current?.getContainer?.();

    layer.on('mouseover', () => {
      layer.setStyle(CROP_LAYER_HOVER_STYLE);
      const container = getMapContainer();
      if (container) {
        container.style.cursor = 'pointer';
      }
    });

    layer.on('mouseout', () => {
      layer.setStyle(CROP_LAYER_BASE_STYLE);
      const container = getMapContainer();
      if (container) {
        container.style.cursor = '';
      }
    });

    // Leaflet Draw fires this immediately after a single spot is marked for deletion.
    layer.on('deleted', () => handleCropLayerDeleted(cropId));
    layer.on('revert-deleted', () => recomputeDrawingState());

    layer.on('click', () => {
      const geometry = layer.toGeoJSON()?.geometry;
      if (!geometry) {
        return;
      }

      const existingPulseTimer = cropPulseTimersRef.current.get(cropId);
      if (existingPulseTimer) {
        window.clearTimeout(existingPulseTimer);
      }
      layer.setStyle(CROP_LAYER_PULSE_STYLE);
      const pulseTimer = window.setTimeout(() => {
        layer.setStyle(CROP_LAYER_BASE_STYLE);
        cropPulseTimersRef.current.delete(cropId);
      }, 180);
      cropPulseTimersRef.current.set(cropId, pulseTimer);

      const bounds = L.geoJSON(geometry).getBounds();
      animateToBounds(bounds, SPOT_FOCUS_BOUNDS_OPTIONS);
      window.requestAnimationFrame(() => revealCropInList(reportId, cropId));
    });
  };

  // Jedna sciezka obsluguje klik na flage mapy i odpowiadajaca jej pozycje na liscie.
  const handleFocusCropSpot = async (report, isActiveReport, cropItem) => {
    const { geometry, id: cropId } = cropItem || {};
    if (!geometry || !mapRef.current) return;

    if (!isActiveReport) {
      await handleRestoreReport(report, { preserveViewport: true });
    }

    if (!mapRef.current) return;

    const bounds = L.geoJSON(geometry).getBounds();
    animateToBounds(bounds, SPOT_FOCUS_BOUNDS_OPTIONS);
    window.requestAnimationFrame(() => revealCropInList(report.id, cropId));
  };

  const reportsForDisplay = useMemo(() => {
    if (!activeReportId) {
      return reports;
    }

    const activeReport = reports.find((report) => report.id === activeReportId);
    if (!activeReport) {
      return reports;
    }

    return [activeReport, ...reports.filter((report) => report.id !== activeReportId)];
  }, [reports, activeReportId]);

  const activeReport = reports.find((report) => report.id === activeReportId) || null;
  const isWebMapLinkEnabled = typeof window !== 'undefined' && !window.desktopConfig;
  const shouldShowDesktopBasemapHint = isDesktopApp && !!desktopBasemapApiRoot;
  const shouldShowBasemapFallbackWarning = isDesktopApp && !isBasemapAvailable;

  const handleExportCrops = async (report) => {
    const reportAoiGeometry = aoiGeometry || report.aoi_geometry || report.geometry || null;
    const reportRemainingPercent = Number.isFinite(Number(remainingFieldDamagePercent))
      ? Number(remainingFieldDamagePercent)
      : Number(report.remaining_field_damage_percent || 0);
    const safeTitle = String(report.title || 'field').replace(/[^\w.-]+/g, '_');
    const fileName = `${safeTitle}-crops.geojson`;

    setReportActionId(report.id);
    setReportsError('');
    try {
      const collection = createCropTransferCollection(cropItems.map((item) => ({
        id: item.id,
        spotNumber: item.spotNumber,
        geometry: item.geometry,
        damagePercent: cropDamagePercents[item.id],
        protectedSpecies: cropProtectedSpecies[item.id],
      })), report, {
        aoiGeometry: reportAoiGeometry,
        remainingFieldDamagePercent: reportRemainingPercent,
        exclusions: exclusionItems.map((item) => ({ id: item.id, geometry: item.geometry })),
      });
      const content = JSON.stringify(collection, null, 2);
      if (window.desktopApi?.saveCropTransfer) {
        await window.desktopApi.saveCropTransfer({ fileName, content });
        return;
      }

      const blob = new Blob([content], { type: 'application/geo+json' });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      setReportsError(err.message || 'Nie udało się wyeksportować cropów.');
    } finally {
      setReportActionId(null);
    }
  };

  const handleImportCrops = async (report, content) => {
    setReportActionId(report.id);
    setReportsError('');
    try {
      const importedTransfer = parseCropTransferCollection(JSON.parse(content));
      const importedCrops = importedTransfer.crops.map((crop, index) => serializeCropGeometryEntry(
        normalizeCropGeometryEntry(crop, null, index + 1)
      ));
      const importedExclusions = (importedTransfer.exclusions || []).map((exclusion, index) => ({
        id: exclusion.id || `exclusion-${report.id}-${index}`,
        geometry: exclusion.geometry,
      }));
      const nextCropNumber = Math.max(
        getNextSpotNumber(report),
        importedCrops.reduce((maximum, crop) => Math.max(maximum, crop.spot_number || 0), 0) + 1
      );
      nextSpotNumbersRef.current.set(String(report.id), nextCropNumber);
      const reportAoiGeometry = importedTransfer.aoiGeometry
        || aoiGeometry
        || report.aoi_geometry
        || report.geometry;
      const remainingPercent = importedTransfer.remainingFieldDamagePercent
        ?? (Number.isFinite(Number(remainingFieldDamagePercent))
          ? Number(remainingFieldDamagePercent)
          : Number(report.remaining_field_damage_percent || 0));
      const assessment = reportAoiGeometry
        ? calculateDamageAssessment({
          aoiGeometry: reportAoiGeometry,
          spots: importedCrops.map((crop) => ({
            geometry: crop.geometry,
            damagePercent: crop.damage_percent,
          })),
          exclusions: importedExclusions,
          remainingFieldDamagePercent: remainingPercent,
        })
        : null;
      const updatedReport = await updateField(report.id, {
        crop_geometries: importedCrops,
        exclusion_geometries: importedExclusions,
        next_crop_number: nextCropNumber,
        ...(importedTransfer.aoiGeometry ? { aoi_geometry: reportAoiGeometry } : {}),
        remaining_field_damage_percent: remainingPercent,
        ...(assessment ? {
          aoi_area_ha: assessment.grossAoiAreaHa,
          exclusion_area_ha: assessment.exclusionAreaHa,
          spot_union_area_ha: assessment.spotUnionAreaHa,
          remaining_area_ha: assessment.remainingAreaHa,
          spot_damage_area_ha: assessment.spotDamageHa,
          remaining_damage_area_ha: assessment.remainingDamageHa,
          affected_area_ha: assessment.totalDamageHa,
          affected_percent: Math.round(assessment.totalDamagePercent * 10) / 10,
        } : {}),
      });
      setReports((previousReports) => previousReports.map((item) => (
        item.id === report.id ? updatedReport : item
      )));
      applyReportGeometryToMap(updatedReport, { fitToBounds: false });
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Nie udało się zaimportować cropów.';
      setReportsError(message);
    } finally {
      setReportActionId(null);
    }
  };

  const handleImportCropFileChange = async (event, report) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      await handleImportCrops(report, await file.text());
    } catch (err) {
      setReportsError(err.message || 'Nie udało się odczytać pliku cropów.');
    }
  };

  const handleChooseCropImport = async (report) => {
    if (!window.desktopApi?.openCropTransfer) {
      cropTransferInputRef.current?.click();
      return;
    }

    try {
      const transfer = await window.desktopApi.openCropTransfer();
      if (transfer) {
        await handleImportCrops(report, transfer.content);
      }
    } catch (err) {
      setReportsError(err.message || 'Nie udało się odczytać pliku cropów.');
    }
  };

  return (
    <AppShell
      isBusy={uploading || loadingReports}
      sidebar={
        <>
          <FileUpload onFileSelect={handleFileSelect} disabled={uploading || loadingReports || !canEditFields} />

          {error && <div className="error-message">{error}</div>}
          {reportsError && <div className="error-message">{reportsError}</div>}
          {shouldShowDesktopBasemapHint && <div className="info-message">{t('map.basemap.desktopHint')}</div>}
          {shouldShowBasemapFallbackWarning && <div className="warning-message">{t('map.basemap.offlineFallback')}</div>}

          <h4>{t('field.list.header', { count: reports.length })}</h4>
          <ul className="field-list">
            {reports.length === 0 && <li className="empty">{t('field.list.empty')}</li>}
              {reportsForDisplay.map((report) => {
                const isActiveReport = activeReportId !== null && String(activeReportId) === String(report.id);
                const isReportExpanded = expandedReportIds.has(String(report.id))
                  || editingReportId === report.id
                  || editingReportNotesId === report.id;
                const savedAoiAreaHa = Number(report.aoi_area_ha);
                const savedExclusionAreaHa = Number(report.exclusion_area_ha) || 0;
                const savedNetAoiAreaHa = Number.isFinite(savedAoiAreaHa)
                  ? Math.max(0, savedAoiAreaHa - savedExclusionAreaHa)
                  : savedAoiAreaHa;
                const fallbackAreaHa = Number(report.area_ha);
                const savedAffectedPercent = Number(report.affected_percent);
                // The displayed 100% is the net field area (gross AOI minus green-island exclusions).
                const displayedAreaHa = isActiveReport
                  ? (Number.isFinite(netAoiAreaHa) ? netAoiAreaHa : savedNetAoiAreaHa)
                  : (Number.isFinite(savedNetAoiAreaHa) ? savedNetAoiAreaHa : fallbackAreaHa);
                const displayedExclusionAreaHa = isActiveReport
                  ? (Number.isFinite(exclusionAreaHa) ? exclusionAreaHa : 0)
                  : savedExclusionAreaHa;
                const displayedExclusionCount = isActiveReport
                  ? exclusionItems.length
                  : (Array.isArray(report.exclusion_geometries) ? report.exclusion_geometries.length : 0);
                const hasExclusions = displayedExclusionCount > 0 || displayedExclusionAreaHa > 0;
                const displayedAffectedPercent = isActiveReport ? affectedPercent : savedAffectedPercent;
                const hasDisplayedPercent = Number.isFinite(displayedAffectedPercent);
                const displayedDamageHa = isActiveReport
                  ? totalDamageHa
                  : (hasDisplayedPercent && Number.isFinite(displayedAreaHa)
                    ? (displayedAreaHa * displayedAffectedPercent) / 100
                    : NaN);
                const inspectionDamagePercent = Number(report.inspection_damage_percent);
                const hasInspectionDamagePercent = report.inspection_damage_percent !== null
                  && report.inspection_damage_percent !== ''
                  && Number.isFinite(inspectionDamagePercent);
                const cropDisplayItems = getCropDisplayItems(report, isActiveReport);
                const displayedRemainingPercent = isActiveReport
                  ? (Number.isFinite(Number(remainingFieldDamagePercent)) ? Number(remainingFieldDamagePercent) : 0)
                  : Number(report.remaining_field_damage_percent || 0);
                const displayedSpotUnionAreaHa = isActiveReport
                  ? Number(damageAssessment?.spotUnionAreaHa)
                  : Number(report.spot_union_area_ha);
                const displayedRemainingAreaHa = isActiveReport
                  ? Number(damageAssessment?.remainingAreaHa)
                  : Number(report.remaining_area_ha);
                const displayedSpotDamageHa = isActiveReport
                  ? Number(damageAssessment?.spotDamageHa)
                  : Number(report.spot_damage_area_ha);
                const displayedRemainingDamageHa = isActiveReport
                  ? Number(damageAssessment?.remainingDamageHa)
                  : Number(report.remaining_damage_area_ha);
                const hasSavedDamageBreakdown = report.spot_union_area_ha !== null
                  && report.spot_union_area_ha !== undefined
                  && report.remaining_area_ha !== null
                  && report.remaining_area_ha !== undefined;
                const hasDamageBreakdown = isActiveReport
                  ? !!damageAssessment && !damageAssessment.errorCode
                  : hasSavedDamageBreakdown;

                return (
              <li
                key={report.id}
                className={`${isActiveReport ? 'is-active ' : ''}${isReportExpanded ? '' : 'is-collapsed'}`}
              >
                {editingReportId === report.id ? (
                  <div className="field-edit-row">
                    <input
                      type="text"
                      value={editingReportTitle}
                      onChange={(event) => setEditingReportTitle(event.target.value)}
                      disabled={reportActionId === report.id}
                    />
                    <div className="field-edit-row__actions">
                      <button type="button" onClick={() => handleSaveReportTitle(report.id)} disabled={reportActionId === report.id}>
                        {t('field.saveName')}
                      </button>
                      <button type="button" onClick={cancelEditingReportTitle} disabled={reportActionId === report.id}>
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="field-title-row"
                  >
                    <span
                      className="field-title-row__content"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleRestoreReport(report)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleRestoreReport(report);
                        }
                      }}
                      title={t('field.actions.showOnMap')}
                    >
                      <strong>{report.title}</strong>
                      {isActiveReport && <span className="field-active-chip">{t('field.activeChip')}</span>}
                    </span>
                    <div className="field-title-row__actions">
                      {canEditFields && (
                        <button
                          type="button"
                          className="field-title-edit-button"
                          disabled={reportActionId === report.id}
                          onClick={() => startEditingReportTitle(report)}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M4 16.5V20h3.5L18.3 9.2l-3.5-3.5L4 16.5Z" />
                            <path d="m13.8 6.7 3.5 3.5" />
                          </svg>
                          <span>{t('field.actions.editName')}</span>
                        </button>
                      )}
                      {editingReportId !== report.id && editingReportNotesId !== report.id && (
                        <button
                          type="button"
                          className="field-collapse-button"
                          aria-label={t(isReportExpanded ? 'field.accordion.collapse' : 'field.accordion.expand')}
                          aria-expanded={isReportExpanded}
                          onClick={() => {
                            setExpandedReportIds((currentIds) => {
                              const nextIds = new Set(currentIds);
                              if (isReportExpanded) {
                                nextIds.delete(String(report.id));
                              } else {
                                nextIds.add(String(report.id));
                              }
                              return nextIds;
                            });
                          }}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d={isReportExpanded ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6'} />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {!isReportExpanded && editingReportId !== report.id && (
                  <span className="field-collapsed-summary">
                    {t('field.crops.header', { count: cropDisplayItems.length })}
                  </span>
                )}
                <div className="field-coordinates">
                  <span className="field-coordinates__label">{t('field.coordinates.label')}</span>
                  {isWebMapLinkEnabled && Number.isFinite(Number(report.latitude)) && Number.isFinite(Number(report.longitude)) ? (
                    <a
                      className="field-coordinates__value"
                      href={`https://www.google.com/maps/search/?api=1&query=${Number(report.latitude)},${Number(report.longitude)}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      title={t('field.coordinates.openMaps')}
                    >
                      {formatCoord(report.latitude)}, {formatCoord(report.longitude)}
                    </a>
                  ) : (
                    <span className="field-coordinates__value">{formatCoord(report.latitude)}, {formatCoord(report.longitude)}</span>
                  )}
                </div>
                <span>{t('field.area100', { area: formatArea(displayedAreaHa) })}</span>
                {hasExclusions && (
                  <div className="field-exclusion-summary">
                    <span className="field-exclusion-summary__label">
                      {t('field.exclusions.summaryLabel', { count: displayedExclusionCount })}
                    </span>
                    <span className="field-exclusion-summary__area">−{formatArea(displayedExclusionAreaHa)}</span>
                  </div>
                )}
                {hasDisplayedPercent && (
                  <div className="field-damage-summary">
                    <span className="field-damage-summary__label">Uszkodzenie pola</span>
                    <strong className="field-damage-summary__percent">{Number(displayedAffectedPercent.toFixed(1))}%</strong>
                    {Number.isFinite(displayedDamageHa) && (
                      <span className="field-damage-summary__area">{formatArea(displayedDamageHa)}</span>
                    )}
                  </div>
                )}
                <form className="field-details" onSubmit={(event) => handleSaveFieldDetails(event, report.id)}>
                  <label>
                    <span>{t('field.cropType.label')}</span>
                    <select name="cropType" defaultValue={report.crop_type || ''} disabled={!canEditFields || reportActionId === report.id}>
                      {CROP_TYPES.map((cropType) => (
                        <option key={cropType.value} value={cropType.value}>
                          {cropType.icon} {t(cropType.labelKey)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>{t('field.owner.label')}</span>
                    <input
                      name="ownerName"
                      type="text"
                      defaultValue={report.owner_name || ''}
                      placeholder={t('field.owner.placeholder')}
                      disabled={!canEditFields || reportActionId === report.id}
                    />
                  </label>
                  <button type="submit" disabled={!canEditFields || reportActionId === report.id}>{t('field.details.save')}</button>
                </form>
                {(isActiveReport || hasExclusions) && (
                  <div className="field-exclusions">
                    <span className="field-exclusions__label">
                      {t('field.exclusions.header', { count: displayedExclusionCount })}
                    </span>
                    {isActiveReport && canEditFields && (
                      <p className={`field-exclusions__hint${exclusionDrawMode ? ' is-active' : ''}`}>
                        {exclusionDrawMode ? t('field.exclusions.modeOn') : t('field.exclusions.hint')}
                      </p>
                    )}
                    {isActiveReport && exclusionItems.length > 0 && (
                      <ul className="field-exclusions__list">
                        {exclusionItems.map((item, index) => (
                          <li key={item.id} className="field-exclusions__item">
                            <span className="field-exclusions__item-name">
                              {t('field.exclusions.item', { number: index + 1 })}
                            </span>
                            <span className="field-exclusions__area">{formatArea(item.areaHa)}</span>
                            {canEditFields && (
                              <button
                                type="button"
                                className="field-crop-delete-button"
                                title={t('field.exclusions.delete', { number: index + 1 })}
                                aria-label={t('field.exclusions.delete', { number: index + 1 })}
                                onClick={() => handleDeleteExclusion(item.id)}
                              >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M4 7h16" />
                                  <path d="M10 11v6M14 11v6" />
                                  <path d="M6.5 7 7.4 20h9.2l.9-13" />
                                  <path d="M9 7V4h6v3" />
                                </svg>
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div className="field-crops">
                  <span className="field-crops__label">{t('field.crops.header', { count: cropDisplayItems.length })}</span>
                  {developerToolsEnabled && canEditFields && isActiveReport && (
                    <details className="crop-transfer-tools">
                      <summary>{t('field.cropTransfer.title')}</summary>
                      <div className="crop-transfer-tools__actions">
                        <button
                          type="button"
                          onClick={() => handleExportCrops(report)}
                          disabled={reportActionId === report.id}
                        >
                          {t('field.cropTransfer.export')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleChooseCropImport(report)}
                          disabled={reportActionId === report.id}
                        >
                          {t('field.cropTransfer.import')}
                        </button>
                        <input
                          ref={cropTransferInputRef}
                          type="file"
                          accept=".geojson,.json,application/geo+json,application/json"
                          hidden
                          onChange={(event) => handleImportCropFileChange(event, report)}
                        />
                      </div>
                      <small>{t('field.cropTransfer.description')}</small>
                    </details>
                  )}
                  {cropDisplayItems.length === 0 ? (
                    <p className="field-crops__empty">{t('field.crops.empty')}</p>
                  ) : (
                    <ul className="field-crops__list">
                      {cropDisplayItems.map((cropItem) => {
                        const numericPercent = Number(cropItem.damagePercent);
                        const damagedAreaHa = Number.isFinite(numericPercent)
                          ? cropItem.areaHa * (numericPercent / 100)
                          : NaN;
                        const spotNumber = cropItem.spotNumber;

                        return (
                          <li
                            key={cropItem.id}
                            ref={(element) => {
                              const cropKey = getCropListKey(report.id, cropItem.id);
                              if (element) cropListItemRefs.current.set(cropKey, element);
                              else cropListItemRefs.current.delete(cropKey);
                            }}
                            className={`field-crops__item${highlightedCropKey === getCropListKey(report.id, cropItem.id) ? ' is-highlighted' : ''}`}
                            role="button"
                            tabIndex={0}
                            title={t('field.crops.focusTitle')}
                            onClick={() => handleFocusCropSpot(report, isActiveReport, cropItem)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleFocusCropSpot(report, isActiveReport, cropItem);
                              }
                            }}
                          >
                            <CropFlag number={spotNumber} />
                            <div className="field-crops__row">
                              <strong>{t('field.crops.spot', { number: spotNumber })}</strong>
                              <div className="field-crops__actions" onClick={(event) => event.stopPropagation()}>
                                <span>{formatArea(cropItem.areaHa)}</span>
                                {cropItem.editable && (
                                  <button
                                    type="button"
                                    className="field-crop-delete-button"
                                    title={`Usuń ${t('field.crops.spot', { number: spotNumber })}`}
                                    aria-label={`Usuń ${t('field.crops.spot', { number: spotNumber })}`}
                                    onClick={() => handleDeleteCropSpot(cropItem.id)}
                                  >
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                      <path d="M4 7h16" />
                                      <path d="M10 11v6M14 11v6" />
                                      <path d="M6.5 7 7.4 20h9.2l.9-13" />
                                      <path d="M9 7V4h6v3" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="field-crops__percent-row" onClick={(event) => event.stopPropagation()}>
                              <label htmlFor={`crop-percent-${cropItem.id}`}>{t('field.crops.percentLabel')}</label>
                              <input
                                id={`crop-percent-${cropItem.id}`}
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                value={cropItem.damagePercent}
                                disabled={!cropItem.editable}
                                onChange={(event) => handleCropDamagePercentChange(cropItem.id, event.target.value)}
                              />
                              <span>%</span>
                            </div>
                            <label className="field-crops__protected-row" onClick={(event) => event.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={Boolean(cropItem.protectedSpecies)}
                                disabled={!cropItem.editable}
                                onChange={(event) => handleCropProtectedSpeciesChange(cropItem.id, event.target.checked)}
                              />
                              <span>{t('field.crops.protectedSpeciesLabel')}</span>
                            </label>
                            {Number.isFinite(damagedAreaHa) && (
                              <span className="field-crops__damage">{t('field.crops.estimatedDamage', { area: formatArea(damagedAreaHa) })}</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                {hasInspectionDamagePercent && (
                  <div className="field-inspection-damage">
                    <span className="field-inspection-damage__label">{t('field.inspectionDamage.label')}</span>
                    <strong>{t('field.inspectionDamage.value', { percent: Number(inspectionDamagePercent.toFixed(1)) })}</strong>
                    <small>{t('field.inspectionDamage.note')}</small>
                  </div>
                )}
                <div className={`field-remainder-damage${isActiveReport ? ' is-editable' : ''}`}>
                  <div className="field-remainder-damage__heading">
                    <strong>{t('field.remainderDamage.title')}</strong>
                    <span>{t('field.remainderDamage.badge')}</span>
                  </div>
                  <p>{t('field.remainderDamage.description')}</p>
                  <div className="field-remainder-damage__control">
                    <label htmlFor={`remaining-damage-${report.id}`}>{t('field.remainderDamage.percentLabel')}</label>
                    <div>
                      <input
                        id={`remaining-damage-${report.id}`}
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={displayedRemainingPercent}
                        disabled={!isActiveReport || !canEditFields}
                        onChange={(event) => handleRemainingFieldDamagePercentChange(event.target.value)}
                      />
                      <span>%</span>
                    </div>
                  </div>
                  {(isActiveReport && damageAssessment?.errorCode) || cropDisplayItems.some((item) => item.calculationError) ? (
                    <div className="field-remainder-damage__error">
                      {t(`field.error.${damageAssessment?.errorCode === 'overlapping-spots' ? 'cropsOverlap' : 'invalidDamageGeometry'}`)}
                    </div>
                  ) : hasDamageBreakdown && (
                    <dl className="field-remainder-damage__breakdown">
                      <div>
                        <dt>{t('field.remainderDamage.spotsArea')}</dt>
                        <dd>{formatArea(displayedSpotUnionAreaHa)}</dd>
                      </div>
                      <div>
                        <dt>{t('field.remainderDamage.remainingArea')}</dt>
                        <dd>{formatArea(displayedRemainingAreaHa)}</dd>
                      </div>
                      <div>
                        <dt>{t('field.remainderDamage.spotsContribution')}</dt>
                        <dd>{formatArea(displayedSpotDamageHa)}</dd>
                      </div>
                      <div>
                        <dt>{t('field.remainderDamage.remainingContribution')}</dt>
                        <dd>{formatArea(displayedRemainingDamageHa)}</dd>
                      </div>
                    </dl>
                  )}
                  {!isActiveReport && <small>{t('field.remainderDamage.activateToEdit')}</small>}
                </div>
                <div className="field-notes">
                  <div className="field-notes__heading">
                    <span className="field-notes__label">{t('field.notes.label')}</span>
                    {canEditFields && editingReportNotesId !== report.id && (
                      <button
                        type="button"
                        className="field-notes-edit-button"
                        title={t('field.actions.editNotes')}
                        aria-label={t('field.actions.editNotes')}
                        disabled={reportActionId === report.id}
                        onClick={() => startEditingReportNotes(report)}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M4 16.5V20h3.5L18.3 9.2l-3.5-3.5L4 16.5Z" />
                          <path d="m13.8 6.7 3.5 3.5" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {editingReportNotesId === report.id ? (
                    <div className="field-edit-notes-row">
                      <textarea
                        rows="4"
                        value={editingReportNotes}
                        onChange={(event) => setEditingReportNotes(event.target.value)}
                        disabled={reportActionId === report.id}
                      />
                      <div className="field-notes__buttons">
                        <button type="button" onClick={() => handleSaveReportNotes(report.id)} disabled={reportActionId === report.id}>
                          {t('field.notes.save')}
                        </button>
                        <button type="button" onClick={cancelEditingReportNotes} disabled={reportActionId === report.id}>
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="field-notes__text">{report.notes?.trim() ? report.notes : t('field.notes.empty')}</p>
                  )}
                </div>
                <div className="field-actions">
                  <div className="field-actions__utilities">
                    {!isActiveReport && (
                      <button
                        type="button"
                        className="field-show-on-map-button"
                        onClick={() => handleRestoreReport(report)}
                        disabled={uploading || savingReport}
                        title={t('field.actions.showOnMap')}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 21s7-5.2 7-12a7 7 0 1 0-14 0c0 6.8 7 12 7 12Z" />
                          <circle cx="12" cy="9" r="2.3" />
                        </svg>
                        <span>{t('field.actions.showOnMap')}</span>
                      </button>
                    )}
                    <div className="field-actions__icons">
                    {isActiveReport && (
                      <button
                        type="button"
                        className="field-save-button"
                        onClick={() => handleSaveReportState(report.id)}
                        disabled={!canEditFields || reportActionId === report.id}
                        title={t('field.actions.saveState')}
                        aria-label={t('field.actions.saveState')}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M5 3h12l3 3v15H5z" />
                          <path d="M8 3v6h8V3" />
                          <path d="M8 21v-7h8v7" />
                        </svg>
                      </button>
                    )}
                    <button
                      type="button"
                      className="field-pdf-button"
                      onClick={() => handleDownloadPdfReport(report)}
                      disabled={!isActiveReport || pdfExportReportId === report.id}
                      title={pdfExportReportId === report.id
                        ? 'Generowanie PDF...'
                        : (!isActiveReport ? 'Najpierw pokaż pole na mapie.' : 'Eksportuj PDF')}
                      aria-label={pdfExportReportId === report.id ? 'Generowanie PDF...' : 'Eksportuj PDF'}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M6 3h8l4 4v14H6z" />
                        <path d="M14 3v5h5" />
                        <path d="M8.5 16.5h7" />
                        <path d="M8.5 13.5h7" />
                        <path d="M8.5 10.5h2.5" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="field-delete-button"
                      onClick={() => handleDeleteReport(report.id)}
                      disabled={!canEditFields || reportActionId === report.id}
                      title={t('field.actions.delete')}
                      aria-label={t('field.actions.delete')}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M4 7h16" />
                        <path d="M10 11v6M14 11v6" />
                        <path d="M6.5 7 7.4 20h9.2l.9-13" />
                        <path d="M9 7V4h6v3" />
                      </svg>
                    </button>
                    </div>
                  </div>
                </div>
              </li>
            );})}
          </ul>
        </>
      }
    >
      <div className="map-container">
        {shouldShowBasemapFallbackWarning && (
          <div className="map-basemap-status" role="status" aria-live="polite">
            {t('map.basemap.offlineOverlay')}
          </div>
        )}
        <MapContainer 
          center={center} 
          zoom={zoom} 
          maxZoom={MAX_INTERACTION_ZOOM}
          preferCanvas
          style={{ height: '100%', width: '100%' }}
        >
          <MapInstanceBridge onMapReady={handleMapReady} />
          <TileLayer
            attribution={selectedBasemap.attribution}
            url={basemapUrl}
            maxNativeZoom={selectedBasemap.maxZoom}
            maxZoom={MAX_INTERACTION_ZOOM}
            eventHandlers={{
              tileload: () => setIsBasemapAvailable(true),
              tileerror: () => setIsBasemapAvailable(false),
            }}
          />
          {uploading && loadingIndicatorPoint && (
            <Marker position={[loadingIndicatorPoint.lat, loadingIndicatorPoint.lng]} icon={loadingMapIcon} interactive={false} />
          )}
          {!uploading && cropItems.map((item, index) => (
            item.center && (
              <Marker
                key={item.id}
                position={[item.center.lat, item.center.lng]}
                icon={createCropFlagIcon(item.spotNumber || index + 1)}
                eventHandlers={{ click: () => handleFocusCropSpot(activeReport, true, item) }}
              />
            )
          ))}
          <AutoAoiControl onClick={handleUseWholeGeoTIFFAsAoi} disabled={uploading || !geoTIFFData || !canEditFields} />
          <ExclusionModeControl
            onClick={handleToggleExclusionMode}
            active={exclusionDrawMode}
            disabled={uploading || !canEditFields || !aoiGeometry}
          />
          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              position="topleft"
              {...drawingHandlers}
              edit={editOptions}
              draw={drawOptions}
            />
          </FeatureGroup>
          <MapContentWrapper geoTIFFData={geoTIFFData} onError={handleRasterError} />
        </MapContainer>
      </div>
    </AppShell>
  );
};

export default MapComponent;
